import { useRef, useState } from "react";
import { ReactCropperElement } from "react-cropper";

import { useTranslator } from "@eo-locale/react";
import { FiEdit3, FiImage, FiTrash } from "react-icons/fi";

import {
  AspectRatio,
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useMediaQuery
} from "@/storybook-components";
import { Controls } from "./components/controls";
import { Editor } from "./components/editor";
import { Footer } from "./components/footer";
import { Header } from "./components/header";
import { CropperContext } from "./context";

interface CropperProps {
  isDisabled: boolean;
  onUpdate: () => void;
  onReplace: () => void;
  onDelete: () => void;
  onCropCancel: () => void;
  keepOriginalImageToBeCropped: boolean;
  onCropComplete: (imageUrl: string) => void;
  renderAsButton?: boolean;
  getFileDetails?: (file: any) => void;
  existingImageUrl?: string;
}

function Cropper({
  isDisabled,
  onUpdate,
  onReplace,
  onDelete,
  onCropCancel,
  onCropComplete,
  keepOriginalImageToBeCropped = true,
  renderAsButton,
  getFileDetails,
  existingImageUrl
}: Partial<CropperProps>) {
  const { translate } = useTranslator();

  const [previewImage, setPreviewImage] = useState(existingImageUrl || "");
  const [originalImage, setOriginalImage] = useState("");
  const [isCropActive, setIsCropActive] = useState(false);
  const [imageToBeCropped, setImageToBeCropped] = useState(
    existingImageUrl || ""
  );

  const cropStatus = existingImageUrl ? "enabled" : "disabled";
  const [status, setStatus] = useState<"disabled" | "enabled">(cropStatus);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cropperRef = useRef<ReactCropperElement>(null);
  const inputFileNode = useRef<HTMLInputElement>(null);

  const [isDesktop] = useMediaQuery("(min-width: 768px)");

  const destroyCrop = () => {
    if (originalImage) {
      setImageToBeCropped(originalImage);
    } else {
      cropperRef.current?.cropper.destroy();
      if (!existingImageUrl) setImageToBeCropped("");
    }

    if (onCropCancel) onCropCancel();

    setIsCropActive(false);
    onClose();
  };

  const submitCrop = () => {
    setStatus("disabled");

    const cropedCanvas = cropperRef.current?.cropper.getCroppedCanvas();
    const croppedImageUrl = cropedCanvas?.toDataURL("image/jpeg") || "";

    const originalCanvas = cropperRef.current?.cropper
      .clear()
      .reset()
      .getCroppedCanvas();
    const originalImageUrl = originalCanvas?.toDataURL("image/jpeg") || "";

    setOriginalImage(originalImageUrl);

    if (keepOriginalImageToBeCropped) setImageToBeCropped(originalImageUrl);
    else setImageToBeCropped(croppedImageUrl);

    setPreviewImage(croppedImageUrl);

    if (onCropComplete) onCropComplete(croppedImageUrl);

    cropperRef.current?.cropper.destroy();

    setStatus("enabled");
    setIsCropActive(false);
    onClose();
  };

  return (
    <CropperContext.Provider
      value={{
        cropperRef,
        destroyCrop,
        status,
        setStatus,
        submitCrop,
        imageToBeCropped,
        setImageToBeCropped,
        isCropActive,
        setIsCropActive
      }}
    >
      <Box display={previewImage ? "block" : "none"} w="full" maxW="27rem">
        <AspectRatio ratio={16 / 9}>
          <Image alt="" src={previewImage} />
        </AspectRatio>
        <ButtonGroup
          mt={2}
          gap={1}
          size="xs"
          justifyContent="space-between"
          display={{ base: "flex", lg: "inline-flex" }}
          isDisabled={isDisabled || status === "disabled"}
        >
          <Button
            variant="tertiary"
            leftIcon={<FiEdit3 />}
            backgroundColor="inherit"
            onClick={() => {
              if (onUpdate) onUpdate();
              onOpen();
            }}
          >
            {translate("common.update")}
          </Button>
          <Button
            variant="tertiary"
            leftIcon={<FiImage />}
            backgroundColor="inherit"
            onClick={() => {
              if (onReplace) onReplace();
              inputFileNode.current?.click();
            }}
          >
            {translate("common.replace")}
          </Button>
          <Button
            variant="tertiary"
            leftIcon={<FiTrash />}
            backgroundColor="inherit"
            onClick={() => {
              setImageToBeCropped("");
              setOriginalImage("");
              setPreviewImage("");
              if (onDelete) onDelete();
            }}
          >
            {translate("common.delete")}
          </Button>
        </ButtonGroup>
      </Box>
      <Box display={previewImage ? "none" : "initial"}>
        {renderAsButton ? (
          <Button
            w="full"
            leftIcon={<FiImage />}
            variant="secondary"
            justifyContent="center"
            onClick={() => inputFileNode.current?.click()}
          >
            {translate("common.addAnImage")}
          </Button>
        ) : (
          <IconButton
            isRound
            minW="auto"
            variant="secondary"
            icon={<FiImage />}
            isDisabled={isDisabled}
            width={{ base: 10, md: 8 }}
            height={{ base: 10, md: 8 }}
            aria-label={translate("common.upload")}
            onClick={() => inputFileNode.current?.click()}
          />
        )}
        <Input
          type="file"
          display="none"
          ref={inputFileNode}
          onChange={(e) => {
            const file = e.target.files?.[0];

            if (file && /^image\//.test(file.type)) {
              // eslint-disable-next-line no-undef
              let reader = new FileReader();
              reader.addEventListener("load", () => {
                setImageToBeCropped(reader.result?.toString() || "");
              });
              reader.readAsDataURL(file);
              getFileDetails?.(file);
              e.target.value = "";

              setImageToBeCropped("");
              setOriginalImage("");
              onOpen();
            }
          }}
          accept=".jpg,.jpeg,.gif,.png,.bmp,.webp"
        />
        {isDesktop ? (
          <Modal isOpen={isOpen} onClose={destroyCrop}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Header />
              </ModalHeader>
              <ModalBody>
                <Editor />
              </ModalBody>
              <ModalFooter gap={6} display="flex" flexDirection="column">
                <Controls />
                <Footer />
              </ModalFooter>
            </ModalContent>
          </Modal>
        ) : (
          <Drawer
            size="full"
            isOpen={isOpen}
            placement="right"
            onClose={destroyCrop}
          >
            <DrawerOverlay />
            <DrawerContent h="100svh">
              <DrawerHeader>
                <Header />
              </DrawerHeader>
              <DrawerBody display="flex" alignItems="center">
                <Editor />
              </DrawerBody>
              <DrawerFooter gap={6} display="flex" flexDirection="column">
                <Controls />
                <Footer />
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        )}
      </Box>
    </CropperContext.Provider>
  );
}

export type { CropperProps as ImageEditorProps };
export { Cropper as ImageEditor };
